import React, { useState } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import promptMapping from "./promptMapping";

const BG_COLOR = "#5780BD";
const BUTTON_STYLE = {
  background: "#000",
  color: "#fff",
  borderRadius: 999,
  fontWeight: 700,
  fontSize: "1.5rem",
  width: "100%",
  py: 1,
  my: 0.75,
  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
  "&:hover": {
    background: "#222",
  },
};

function getOptions(node) {
  if (!node.options) return [];
  return Object.keys(node.options);
}

// Case-insensitive, trimmed matching for path steps, with logging
function getNodeFromPath(mapping, path) {
  let node = mapping.root;
  for (const step of path) {
    if (!node.options) {
      console.log("No options at node for step:", step, node);
      return null;
    }
    const keys = Object.keys(node.options);
    const matchKey = keys.find(
      (k) => k.trim().toLowerCase() === step.trim().toLowerCase()
    );
    console.log(
      `Step: '${step}' | Available keys: [${keys.join(
        ", "
      )}] | Matched: '${matchKey}'`
    );
    if (!matchKey) return null;
    node = node.options[matchKey];
  }
  console.log("Final node for path", path, node);
  return node;
}

const SLIDEWAYS_INTRO = {
  title: "Hooray!",
  img: "/assets/ducks/qr/happy.svg",
  lines: [
    "The game is called \u201cSlideways\u201d",
    "Slide words horizontally to form a center vertical word.",
    "Form words to score points.",
  ],
  button: {
    label: "Let's go!",
    href: "https://www.ponderclub.co/slideways/coffee",
  },
};

const CoffeeLineQR = () => {
  const [path, setPath] = useState([]);
  const [showSlidewaysIntro, setShowSlidewaysIntro] = useState(false);
  const node = getNodeFromPath(promptMapping, path) || promptMapping.root;
  const options = getOptions(node);

  // Always display the current node's prompt/svg, even if it's a leaf
  const displayNode = node;

  // Image src from public assets
  const imgSrc = `/assets/ducks/qr/${displayNode.svg || "coffee-cup.svg"}`;

  // Is this a leaf node (no options)?
  const isLeaf = options.length === 0;

  return (
    <Box width={"100%"} bgcolor={BG_COLOR}>
      <Box
        sx={{
          minHeight: "100vh",
          maxWidth: "sm",
          mx: "auto",
          bgcolor: BG_COLOR,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
        }}
      >
        {showSlidewaysIntro ? (
          <Paper
            elevation={0}
            sx={{
              bgcolor: "transparent",
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, sm: 4 },
              mb: 2,
              width: "100%",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "#fff",
                fontWeight: 700,
                mb: 2,
                mt: 1,
                textAlign: "center",
              }}
            >
              {SLIDEWAYS_INTRO.title}
            </Typography>
            <Box
              component="img"
              src={SLIDEWAYS_INTRO.img}
              alt="slideways duck"
              sx={{
                width: 220,
                height: "auto",
                mb: 2,
                mt: 1,
                userSelect: "none",
              }}
              draggable={false}
            />
            <Box sx={{ mb: 3 }}>
              {SLIDEWAYS_INTRO.lines.map((line, i) => (
                <Typography
                  key={i}
                  sx={{
                    color: "#fff",
                    fontWeight: i === 0 ? 700 : 400,
                    fontSize: i === 0 ? "1.5rem" : "1.2rem",
                    textAlign: "center",
                    mb: 1,
                  }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
            <Button
              variant="contained"
              sx={{
                ...BUTTON_STYLE,
                fontSize: "1.3rem",
                maxWidth: 220,
                mt: 2,
                mb: 1,
              }}
              href={SLIDEWAYS_INTRO.button.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {SLIDEWAYS_INTRO.button.label}
            </Button>
          </Paper>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                bgcolor: "transparent",
                boxShadow: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 2, sm: 4 },
                mb: 2,
              }}
            >
              {imgSrc && (
                <Box
                  component="img"
                  src={imgSrc}
                  alt="prompt visual"
                  sx={{
                    width: { xs: 300, sm: 260 },
                    height: "auto",
                    height: 300,
                    mb: 4,
                    mt: 2,
                    userSelect: "none",
                    display: "block",
                  }}
                  draggable={false}
                />
              )}
              <Typography
                align="center"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  mb: 3,
                  mt: 1,
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  lineHeight: 1.2,
                }}
              >
                {displayNode.prompt}
              </Typography>
            </Paper>
            <Box
              sx={{
                width: "100%",
                maxWidth: 300,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.2,
              }}
            >
              {isLeaf ? (
                <Button
                  variant="contained"
                  sx={BUTTON_STYLE}
                  onClick={() => setShowSlidewaysIntro(true)}
                >
                  Yes
                </Button>
              ) : (
                ["Yes", "No", "Maybe so"].map((label) =>
                  options.includes(label) ? (
                    <Button
                      key={label}
                      variant="contained"
                      sx={BUTTON_STYLE}
                      onClick={() => setPath([...path, label])}
                    >
                      {label}
                    </Button>
                  ) : null
                )
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CoffeeLineQR;
